import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useRef } from 'react';
import LayoutSwitcher from '../layouts/LayoutSwitcher';
import BookingStepRenderer from './BookingStepRenderer';
import { BookingStep } from '../shared/hooks/booking-types';
import useBookingState from '../shared/hooks/useBookingState';
import useBookingSteps from '../shared/hooks/useBookingSteps';
import useResponsive from '../shared/hooks/useResponsive';
import LoadingComponent from '../shared/components/LoadingComponent';
import MobileStepIndicator from '../shared/components/MobileStepIndicator';
import StepIndicator from '../shared/components/StepIndicator';

const BookingWidget: React.FC<{widgetConfiguration?: any, bookingPageConfiguration?: any}> = (props) => {
  const widgetRef = useRef<HTMLDivElement>(null);

  // Custom hooks for widget functionality

  const {
    currentStep,
    steps,
    stepsLoading,
    nextStep,
    prevStep,
    setStep,
  } = useBookingSteps(props.widgetConfiguration, props.bookingPageConfiguration);

  const {
    selectedServices,
    setSelectedServices,
    selectedProvider,
    setSelectedProvider,
    booking,
    selectedDate,
    setSelectedDate,
    selectedTime,
    setSelectedTime,
    selectedLocation,
    selectedTimeZone,
    serviceProviderMap,
    setServiceProviderMap,
    customerInfo,
    setCustomerInfo,
    handleSelectedLocation,
    handleBookingComplete,
    selectedCategories,
    setSelectedCategories,
    resetState
  } = useBookingState();

  const { isMobile, scrollToTop } = useResponsive();
  const layoutType = props?.widgetConfiguration?.layoutType
    ?? props?.bookingPageConfiguration?.configuration?.bookingPageConfig?.layoutType;

  // Handle scroll to top when step changes
  useEffect(() => {
    scrollToTop();
  }, [currentStep]);

  // Determine the first step to use for resetting
  const getFirstStep = () => {
    if (steps.length > 0) {
      return steps[0].step;
    }
    // Fallback if steps array is empty for some reason
    return props?.widgetConfiguration?.isLocationsActivated ? BookingStep.LOCATION_STEP : BookingStep.SERVICE_SELECTION;
  };

  // Animation configuration
  const pageVariants = {
    initial: { opacity: 0 },
    in: { opacity: 1 },
    out: { opacity: 0 },
  };

  const pageTransition = {
    type: "tween",
    ease: "easeInOut",
    duration: 0.3,
  };

  // Handler for booking completion
  const onCompletedBooking = (bookingData: any) => {
    handleBookingComplete(bookingData, setStep);
  };

  // Handler for state reset
  const handleResetState = () => {
    resetState(getFirstStep(), setStep);
  };

  return (
    <LayoutSwitcher
      layoutType={layoutType}
      backgroundColor={props?.bookingPageConfiguration?.configuration?.bookingPageConfig?.bookingPageColor}
    >
      <AnimatePresence mode="wait">
        {(props?.widgetConfiguration === null) || stepsLoading ? (
          <motion.div
            key="loading"
            className="lp-absolute lp-inset-0"
            initial={{ opacity: 1, filter: "blur(0px)" }}
            exit={{
              opacity: 0,
              filter: "blur(8px)",
              transition: {
                duration: 0.5,
                ease: "easeOut"
              }
            }}
          >
            <LoadingComponent />
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0, filter: "blur(8px)" }}
            animate={{
              opacity: 1,
              filter: "blur(0px)",
              transition: {
                duration: 0.5,
                delay: 0.2,
                ease: "easeOut"
              }
            }}
            className="lp-w-full"
          >
            {layoutType !== 'split-screen' && layoutType !== 'immersive' && (isMobile ? (
              <MobileStepIndicator steps={steps} currentStep={currentStep} colors={props?.bookingPageConfiguration?.configuration?.bookingPageConfig?.stepIndicatorColors}  />
            ) : (
              <StepIndicator steps={steps} currentStep={currentStep} colors={props?.bookingPageConfiguration?.configuration?.bookingPageConfig?.stepIndicatorColors} />
            ))}

            <div className={layoutType !== 'split-screen' && layoutType !== 'immersive' ? 'lp-min-h-[calc(100vh-16rem)] lp-mt-8' : ''}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}
                  className="lp-w-full"
                >
                  <BookingStepRenderer
                    currentStep={currentStep}
                    widgetConfiguration={props?.widgetConfiguration}
                    selectedServices={selectedServices}
                    setSelectedServices={setSelectedServices}
                    selectedProvider={selectedProvider}
                    setSelectedProvider={setSelectedProvider}
                    selectedCategories={selectedCategories}
                    setSelectedCategories={setSelectedCategories}
                    booking={booking}
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    selectedTime={selectedTime}
                    setSelectedTime={setSelectedTime}
                    selectedLocation={selectedLocation}
                    setSelectedLocation={handleSelectedLocation}
                    selectedTimeZone={selectedTimeZone}
                    serviceProviderMap={serviceProviderMap}
                    setServiceProviderMap={setServiceProviderMap}
                    customerInfo={customerInfo}
                    setCustomerInfo={setCustomerInfo}
                    steps={steps}
                    nextStep={nextStep}
                    prevStep={prevStep}
                    onCompletedBooking={onCompletedBooking}
                    resetState={handleResetState}
                  />
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </LayoutSwitcher>
  );
};

export default BookingWidget;
